import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { PageHeader } from "@/components/common";
import { Search, BookOpen, PlayCircle } from "lucide-react";

export function slug(s) {
  return (s || "").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
}

function inline(s) {
  return s.split(/(\*\*[^*]+\*\*)/g).map((p, i) =>
    p.startsWith("**") && p.endsWith("**")
      ? <strong key={i} className="text-[#0E2A47]">{p.slice(2, -2)}</strong>
      : <span key={i}>{p}</span>);
}

// Parte el markdown en secciones por "## " (la parte previa es la cabecera).
function parseSecciones(md) {
  const lineas = (md || "").split("\n");
  const secciones = [];
  let actual = { titulo: null, id: "inicio", lineas: [] };
  for (const l of lineas) {
    if (l.startsWith("## ")) {
      if (actual.titulo || actual.lineas.length) secciones.push(actual);
      const t = l.slice(3).trim();
      actual = { titulo: t, id: slug(t), lineas: [] };
    } else if (l.startsWith("# ")) {
      // título general, se ignora como sección
    } else {
      actual.lineas.push(l);
    }
  }
  secciones.push(actual);
  return secciones.filter((s) => s.titulo);
}

function Bloques({ lineas }) {
  return (
    <div className="space-y-3 text-sm leading-relaxed text-[#0E2A47]/80">
      {lineas.filter((l) => l.trim()).map((b, i) =>
        b.trim().startsWith("- ")
          ? <div key={i} className="flex gap-2 pl-2"><span className="text-[#FF5625]">•</span><span>{inline(b.trim().slice(2))}</span></div>
          : <p key={i}>{inline(b)}</p>)}
    </div>
  );
}

export default function Ayuda() {
  const [manual, setManual] = useState(null);
  const [q, setQ] = useState("");
  const location = useLocation();

  useEffect(() => { api.get("/ayuda/manual").then((r) => setManual(r.data)).catch(() => {}); }, []);

  const secciones = useMemo(() => parseSecciones(manual?.contenido || ""), [manual]);
  const filtradas = useMemo(() => {
    if (!q.trim()) return secciones;
    const t = q.toLowerCase();
    return secciones.filter((s) => (s.titulo + " " + s.lineas.join(" ")).toLowerCase().includes(t));
  }, [secciones, q]);

  useEffect(() => {
    if (manual && location.hash) {
      const el = document.getElementById(location.hash.slice(1));
      if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 200);
    }
  }, [manual, location.hash]);

  return (
    <div data-testid="ayuda-page">
      <PageHeader overline="Centro de ayuda" title="Manual de usuario" />
      <div className="mb-6 relative max-w-md">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#0E2A47]/40" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar en el manual…" data-testid="ayuda-buscar" className="pl-9" />
      </div>

      <div className="mb-8 rounded-2xl overflow-hidden border border-[#E2E8F0] bg-[#0E2A47]" data-testid="ayuda-video-card">
        <div className="px-5 py-3 flex items-center gap-2 text-white/90">
          <PlayCircle size={18} className="text-[#FF5625]" />
          <span className="text-sm font-bold">Tutorial en vídeo · recorrido completo de AuditReady</span>
        </div>
        <video
          data-testid="ayuda-video"
          className="w-full block bg-black"
          controls
          preload="metadata"
          src="/demo-tutorial/tutorial_maestro.mp4"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[220px_1fr] gap-8">
        <aside className="hidden lg:block">
          <div className="sticky top-6 space-y-1" data-testid="ayuda-indice">
            <div className="text-xs font-bold uppercase tracking-wider text-[#0E2A47]/40 mb-2 flex items-center gap-1"><BookOpen size={13} />Secciones</div>
            {secciones.map((s) => (
              <a key={s.id} href={`#${s.id}`} data-testid={`ayuda-nav-${s.id}`}
                className="block text-sm text-[#0E2A47]/70 hover:text-[#FF5625] py-1 transition-colors">{s.titulo}</a>
            ))}
          </div>
        </aside>

        <main className="min-w-0 space-y-8">
          {filtradas.length === 0 && <p className="text-[#0E2A47]/40 text-sm">Sin resultados para "{q}".</p>}
          {filtradas.map((s) => (
            <section key={s.id} id={s.id} className="scroll-mt-24" data-testid={`ayuda-seccion-${s.id}`}>
              <h2 className="text-xl font-black tracking-tight text-[#0E2A47] mb-3 pb-1 border-b border-[#E2E8F0]">{s.titulo}</h2>
              <Bloques lineas={s.lineas} />
            </section>
          ))}
        </main>
      </div>
    </div>
  );
}
